/**
 * FeatureLockedItem
 * Menu item component that shows a lock icon for features requiring upgrade
 * Clean design: lock icon with plan colors + tooltip showing required plan
 * Lock color matches the plan that unlocks the feature
 */

import React from 'react';
import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Tooltip,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { PlanId, PLAN_DEFINITIONS } from '../../config/plans';

// Plan colors for lock icons - matches the plan that unlocks the feature
const PLAN_LOCK_COLORS: Record<PlanId, { primary: string; light: string }> = {
  trial: { primary: '#718096', light: '#a0aec0' },
  individual: { primary: '#3b82f6', light: '#93c5fd' },
  small_clinic: { primary: '#10b981', light: '#6ee7b7' },
  clinic_pro: { primary: '#f59e0b', light: '#fcd34d' },
  enterprise: { primary: '#8b5cf6', light: '#c4b5fd' },
};

interface FeatureLockedItemProps {
  icon: React.ReactNode;
  label: string;
  path: string;
  isLocked: boolean;
  requiredPlan?: PlanId;
  featureKey?: string;
  onClick?: () => void;
}

const FeatureLockedItem: React.FC<FeatureLockedItemProps> = ({
  icon,
  label,
  path,
  isLocked,
  requiredPlan,
  featureKey,
  onClick,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const handleClick = () => {
    if (isLocked && requiredPlan) {
      // Navigate to upgrade page with context
      navigate(`/upgrade?feature=${featureKey || label}&plan=${requiredPlan}`);
    } else {
      if (onClick) {
        onClick();
      } else {
        navigate(path);
      }
    }
  };

  const planName = requiredPlan ? PLAN_DEFINITIONS[requiredPlan]?.displayName : '';
  const planColors = requiredPlan ? PLAN_LOCK_COLORS[requiredPlan] : PLAN_LOCK_COLORS.small_clinic;

  return (
    <ListItem disablePadding>
      <Tooltip
        title={isLocked ? `Clique para ver o plano ${planName}` : ''}
        placement="right"
        arrow
        enterDelay={500}
      >
        <ListItemButton
          onClick={handleClick}
          sx={{
            borderRadius: '8px',
            mx: 1,
            my: 0.25,
            py: 0.75,
            position: 'relative',
            opacity: isLocked ? 0.85 : 1,
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: isLocked
                ? alpha(planColors.primary, 0.08)
                : alpha(theme.palette.primary.main, 0.08),
              opacity: 1,
            },
          }}
        >
          <ListItemIcon
            sx={{
              color: isLocked
                ? theme.palette.text.disabled
                : theme.palette.primary.main,
              minWidth: 40,
            }}
          >
            {isLocked ? (
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                {icon}
                <Box
                  sx={{
                    position: 'absolute',
                    right: -6,
                    bottom: -4,
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    bgcolor: planColors.primary,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
                  }}
                >
                  <LockIcon
                    sx={{
                      fontSize: 10,
                      color: 'white',
                    }}
                  />
                </Box>
              </Box>
            ) : (
              icon
            )}
          </ListItemIcon>
          <ListItemText
            primary={label}
            primaryTypographyProps={{
              fontSize: '0.9rem',
              fontWeight: 500,
              color: isLocked ? theme.palette.text.secondary : 'inherit',
            }}
          />
          {/* Plan badge when locked */}
          {isLocked && requiredPlan && (
            <Chip
              label={planName}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.65rem',
                fontWeight: 700,
                bgcolor: planColors.primary,
                color: 'white',
                '& .MuiChip-label': {
                  px: 1,
                },
              }}
            />
          )}
        </ListItemButton>
      </Tooltip>
    </ListItem>
  );
};

export default FeatureLockedItem;
